import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Activity, Info, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLiveMetrics, LiveMetrics } from '../hooks/useLiveMetrics';

// ── Signal badge type ─────────────────────────────────────────────────────────
interface Signal {
  text: string;
  color: string;
}

// ── Signal computers ──────────────────────────────────────────────────────────
function signalMA50(btcPrice: number, ma50: number): Signal {
  return btcPrice > ma50
    ? { text: 'Above', color: '#00C896' }
    : { text: 'Below', color: '#FF4444' };
}

function signalMA200(btcPrice: number, ma200: number): Signal {
  return btcPrice > ma200
    ? { text: 'Bull Trend', color: '#00C896' }
    : { text: 'Bear Trend', color: '#FF4444' };
}

function signalMA200W(btcPrice: number, ma200w: number): Signal {
  if (btcPrice > ma200w * 1.5) return { text: 'Well Above', color: '#F7931A' };
  if (btcPrice > ma200w) return { text: 'Above', color: '#00C896' };
  return { text: 'Below', color: '#FF4444' };
}

function signalMVRV(mvrv: number | null): Signal | null {
  if (mvrv === null) return null;
  if (mvrv < 1) return { text: 'Undervalued', color: '#00C896' };
  if (mvrv < 2) return { text: 'Accumulate', color: '#00C896' };
  if (mvrv < 3) return { text: 'Fair Value', color: '#F7931A' };
  if (mvrv >= 3.5) return { text: 'Overheated', color: '#FF4444' };
  return { text: 'Elevated', color: '#F7931A' };
}

function signalRSI(rsi: number): Signal {
  if (rsi < 30) return { text: 'Oversold', color: '#00C896' };
  if (rsi < 45) return { text: 'Cooling', color: '#F7931A' };
  if (rsi < 60) return { text: 'Neutral', color: '#888888' };
  if (rsi < 70) return { text: 'Strong', color: '#F7931A' };
  return { text: 'Overbought', color: '#FF4444' };
}

function signalFundingRate(rate: number | null): Signal | null {
  if (rate === null) return null;
  if (rate > 0.05) return { text: 'High Longs', color: '#FF4444' };
  if (rate >= 0) return { text: 'Mild Bullish', color: '#F7931A' };
  return { text: 'Bearish', color: '#FF4444' };
}

function signalDominance(pct: number | null): Signal | null {
  if (pct === null) return null;
  if (pct >= 60) return { text: 'BTC Leading', color: '#F7931A' };
  if (pct >= 52) return { text: 'Strong', color: '#00C896' };
  if (pct >= 45) return { text: 'Neutral', color: '#888888' };
  return { text: 'Altseason Risk', color: '#FF4444' };
}

function signalHalving(days: number | null): Signal | null {
  if (days === null) return null;
  if (days <= 180) return { text: 'Under 6 Months', color: '#FF4444' };
  if (days <= 365) return { text: 'Under 1 Year', color: '#F7931A' };
  return { text: 'On Schedule', color: '#00C896' };
}

function signalM2(yoy: number | null): Signal | null {
  if (yoy === null) return null;
  if (yoy >= 6) return { text: 'Expanding Fast', color: '#FF4444' };
  if (yoy >= 3) return { text: 'Expanding', color: '#F7931A' };
  if (yoy >= 0) return { text: 'Slow Growth', color: '#888888' };
  return { text: 'Contracting', color: '#00C896' };
}

// ── Format helpers ─────────────────────────────────────────────────────────────
function fmtPrice(v: number): string {
  if (v === 0) return '—';
  return '$' + v.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function fmtNum(v: number, decimals = 2): string {
  if (v === 0) return '—';
  return v.toFixed(decimals);
}

function fmtPct(v: number | null): string {
  if (v === null) return '—';
  const sign = v >= 0 ? '+' : '';
  return sign + v.toFixed(1) + '%';
}

function priceDeltaPct(current: number, reference: number): string {
  if (reference === 0 || current === 0) return '—';
  const pct = ((current - reference) / reference) * 100;
  const sign = pct >= 0 ? '+' : '';
  return sign + pct.toFixed(1) + '%';
}

// ── KPI card data builder ──────────────────────────────────────────────────────
interface KPIData {
  label: string;
  value: string;
  delta: string;
  positive: boolean;
  info: string;
  signal: Signal | null;
  badge?: { text: string; color?: string };
}

function buildKPIs(m: LiveMetrics): KPIData[] {
  return [
    // Page 1 — Price & trend
    {
      label: 'BTC Price',
      value: fmtPrice(m.btcPrice),
      delta: fmtPct(m.btcChange24h),
      positive: m.btcChange24h >= 0,
      info: 'Spot price aggregated across major exchanges. 24h change shown.',
      signal: null,
    },
    {
      label: '50-Day MA',
      value: fmtPrice(m.ma50),
      delta: priceDeltaPct(m.btcPrice, m.ma50),
      positive: m.btcPrice > m.ma50,
      info: '50-day simple moving average. BTC trading above the 50D MA is a short-term bullish signal. Below = potential weakness.',
      signal: m.ma50 > 0 ? signalMA50(m.btcPrice, m.ma50) : null,
    },
    {
      label: '200-Day MA',
      value: fmtPrice(m.ma200),
      delta: priceDeltaPct(m.btcPrice, m.ma200),
      positive: m.btcPrice > m.ma200,
      info: '200-day simple moving average — the key long-term trend indicator. Trading above = bull market signal.',
      signal: m.ma200 > 0 ? signalMA200(m.btcPrice, m.ma200) : null,
    },
    {
      label: '200-Week MA',
      value: fmtPrice(m.ma200w),
      delta: priceDeltaPct(m.btcPrice, m.ma200w),
      positive: m.btcPrice > m.ma200w,
      info: '200-week moving average — historically the Bitcoin bear market floor. Identifies generational buying zones.',
      signal: m.ma200w > 0 ? signalMA200W(m.btcPrice, m.ma200w) : null,
    },
    // Page 2 — On-chain signals
    {
      label: 'MVRV',
      value: m.mvrv !== null ? fmtNum(m.mvrv) : '—',
      delta: '—',
      positive: true,
      info: 'Market Value to Realized Value ratio — approximated as BTC Price ÷ 200D MA. True MVRV requires on-chain UTXO data (Glassnode). >3.5 = historically overheated; <1 = undervalued.',
      signal: signalMVRV(m.mvrv),
      badge: m.mvrv !== null ? { text: '~est.', color: '#F7931A' } : undefined,
    },
    {
      label: 'Next Halving',
      value: m.daysToHalving !== null ? m.daysToHalving.toLocaleString() + 'd' : '—',
      delta: '—',
      positive: true,
      info: 'Days until Bitcoin’s next halving (block 1,050,000, est. April 2028). Every ~4 years the block reward is cut in half — reducing new supply issuance. Historically the most powerful price catalyst in Bitcoin’s cycle.',
      signal: signalHalving(m.daysToHalving),
    },
    {
      label: 'BTC Dominance',
      value: m.btcDominance !== null ? m.btcDominance.toFixed(1) + '%' : '—',
      delta: '—',
      positive: m.btcDominance !== null ? m.btcDominance >= 52 : true,
      info: 'Bitcoin’s share of total global crypto market cap. Rising dominance = capital rotating into BTC vs. altcoins — a flight-to-quality signal. Falling dominance often signals altseason or broad risk appetite.',
      signal: signalDominance(m.btcDominance),
    },
    {
      label: 'Power Law',
      value: m.powerLawFairValue !== null ? fmtPrice(m.powerLawFairValue) : '—',
      delta: m.powerLawFairValue !== null && m.btcPrice > 0
        ? priceDeltaPct(m.btcPrice, m.powerLawFairValue)
        : '—',
      positive: m.btcPrice < (m.powerLawFairValue ?? Infinity),
      info: "Bitcoin Power Law fair value (Santostasi model). Computed from days since genesis — no API needed. BTC below this = historically undervalued vs. the power law trend.",
      signal: m.powerLawFairValue !== null && m.btcPrice > 0
        ? m.btcPrice < m.powerLawFairValue
          ? { text: 'Undervalued', color: '#00C896' }
          : { text: 'Above Model', color: '#F7931A' }
        : null,
    },
    // Page 3 — Sentiment & flows
    {
      label: 'RSI (14D)',
      value: m.rsi14 > 0 ? fmtNum(m.rsi14, 1) : '—',
      delta: '—',
      positive: m.rsi14 < 70,
      info: 'Relative Strength Index on 14-day timeframe. >70 = overbought; <30 = oversold.',
      signal: m.rsi14 > 0 ? signalRSI(m.rsi14) : null,
    },
    {
      label: 'Fear & Greed',
      value: m.fearGreed.toString(),
      delta: '—',
      positive: m.fearGreed >= 50,
      info: 'Crypto Fear & Greed Index (0–100). <25 = Extreme Fear; >75 = Extreme Greed.',
      signal: m.fearGreedLabel ? { text: m.fearGreedLabel, color: m.fearGreed >= 75 ? '#FF4444' : m.fearGreed >= 55 ? '#F7931A' : m.fearGreed >= 45 ? '#888888' : '#00C896' } : null,
    },
    {
      label: 'M2 Money Supply',
      value: m.m2Yoy !== null ? (m.m2Yoy >= 0 ? '+' : '') + m.m2Yoy.toFixed(1) + '% YoY' : '—',
      delta: '—',
      positive: m.m2Yoy !== null ? m.m2Yoy >= 0 : true,
      info: 'U.S. M2 money supply year-over-year % change (FRED M2SL). Expanding M2 = more dollars chasing the same assets. This is the core "why Bitcoin" metric — fixed supply vs. infinite money printer.',
      signal: signalM2(m.m2Yoy),
      badge: { text: 'FRED', color: '#666' },
    },
    {
      label: 'ETF Flows (7D)',
      value: m.etfFlows7d !== null
        ? (m.etfFlows7d >= 0 ? '+$' : '-$') + Math.abs(m.etfFlows7d / 1e9).toFixed(2) + 'B'
        : '—',
      delta: '—',
      positive: m.etfFlows7d !== null ? m.etfFlows7d >= 0 : true,
      info: 'Net inflows across all U.S. Bitcoin spot ETFs over the past 7 days. Source: Farside Investors. No public API available — updated manually.',
      signal: m.etfFlows7d !== null
        ? { text: m.etfFlows7d >= 0 ? 'Inflow' : 'Outflow', color: m.etfFlows7d >= 0 ? '#00C896' : '#FF4444' }
        : null,
      badge: { text: 'Manual', color: '#666' },
    },
  ];
}

// ── Skeleton card ─────────────────────────────────────────────────────────────
const SkeletonCard: React.FC = () => (
  <div className="bg-[#111] border border-white/5 rounded-xl p-4 flex-1 min-w-0">
    <div className="h-3 w-16 rounded bg-white/5 animate-pulse mb-3" />
    <div className="h-6 w-24 rounded bg-white/5 animate-pulse mb-2" />
    <div className="h-4 w-14 rounded-full bg-white/5 animate-pulse" />
  </div>
);

// ── Info popover — Portal renders on document.body, escaping ALL stacking contexts ──
const InfoPopover: React.FC<{ text: string; anchorEl: HTMLElement | null; onClose: () => void }> = ({ text, anchorEl, onClose }) => {
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!anchorEl) return;
    const r = anchorEl.getBoundingClientRect();
    setPos({ top: r.top + window.scrollY, left: r.left + r.width / 2 + window.scrollX });
  }, [anchorEl]);

  return createPortal(
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 99998 }} onClick={onClose} />
      <div
        style={{
          position: 'absolute',
          top: pos.top - 8,
          left: pos.left,
          transform: 'translate(-50%, -100%)',
          zIndex: 99999,
          width: '240px',
          background: '#1a1a1a',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '12px',
          padding: '12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.9)',
          fontSize: '12px',
          color: '#D1D5DB',
          lineHeight: '1.6',
          pointerEvents: 'auto',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          <span style={{ color: '#9CA3AF', fontWeight: 700, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>About</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: 0, lineHeight: 1 }}>
            <X size={11} />
          </button>
        </div>
        {text}
        <div style={{
          position: 'absolute', bottom: -6, left: '50%', transform: 'translateX(-50%)',
          width: 0, height: 0,
          borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
          borderTop: '6px solid #1a1a1a',
        }} />
      </div>
    </>,
    document.body
  );
};

// ── KPI card ──────────────────────────────────────────────────────────────────
interface KPICardProps {
  kpi: KPIData;
  isPinned: boolean;
  onPin: () => void;
  onInfoToggle: (open: boolean) => void;
}

const KPICard: React.FC<KPICardProps> = ({ kpi, isPinned, onPin, onInfoToggle }) => {
  const [showInfo, setShowInfo] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const isUnavailable = kpi.value === '—';

  const handleInfoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const next = !showInfo;
    setShowInfo(next);
    setAnchorEl(next ? btnRef.current : null);
    onInfoToggle(next);
  };

  const handleInfoClose = () => {
    setShowInfo(false);
    setAnchorEl(null);
    onInfoToggle(false);
  };

  return (
    <div
      onClick={onPin}
      className="bg-[#111] rounded-xl p-4 relative flex-1 min-w-0 cursor-pointer select-none"
      style={{
        background: isPinned
          ? 'radial-gradient(ellipse at top left, #F7931A12 0%, #111111 60%)'
          : '#111111',
        border: isPinned ? '1px solid rgba(247,147,26,0.35)' : '1px solid rgba(255,255,255,0.05)',
        boxShadow: isPinned
          ? '0 0 24px 0 rgba(247,147,26,0.18), 0 0 0 1px rgba(247,147,26,0.25)'
          : '0 0 0 1px rgba(247,147,26,0)',
        transform: isPinned ? 'scale(1.025)' : 'scale(1)',
        transition: 'border 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease, background 0.2s ease',
        zIndex: isPinned ? 10 : undefined,
      }}
    >
      {/* Label row */}
      <div className="flex items-center justify-between mb-2">
        <p className="text-gray-500 text-xs font-semibold uppercase tracking-widest truncate pr-1">
          {kpi.label}
        </p>
        <div className="flex items-center gap-1 shrink-0">
          {kpi.badge && (
            <span
              className="text-xs px-1 py-0.5 rounded"
              style={{ color: kpi.badge.color ?? '#666', backgroundColor: (kpi.badge.color ?? '#666') + '18', fontSize: '9px', fontWeight: 700, letterSpacing: '0.02em' }}
            >
              {kpi.badge.text}
            </span>
          )}
          <div className="relative">
            <button
              ref={btnRef}
              onClick={handleInfoClick}
              className="text-gray-700 hover:text-gray-400 transition-colors"
              aria-label={`Info about ${kpi.label}`}
            >
              <Info size={11} />
            </button>
            {showInfo && anchorEl && (
              <InfoPopover text={kpi.info} anchorEl={anchorEl} onClose={handleInfoClose} />
            )}
          </div>
        </div>
      </div>

      {/* Value + delta */}
      <div className="flex items-end gap-2 mb-2">
        <span className="text-white font-bold text-xl leading-none">{kpi.value}</span>
        {!isUnavailable && kpi.delta !== '—' && (
          <span
            className="text-xs font-semibold px-1.5 py-0.5 rounded mb-0.5 shrink-0"
            style={{
              color: kpi.positive ? '#00C896' : '#FF4444',
              backgroundColor: kpi.positive ? '#00C89615' : '#FF444415',
            }}
          >
            {kpi.delta}
          </span>
        )}
      </div>

      {/* Signal badge */}
      {kpi.signal && (
        <span
          className="text-xs font-medium px-2 py-0.5 rounded-full border"
          style={{
            color: kpi.signal.color,
            borderColor: `${kpi.signal.color}35`,
            backgroundColor: `${kpi.signal.color}10`,
          }}
        >
          {kpi.signal.text}
        </span>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const PAGE_SIZE = 4;

const OnChainSnapshot: React.FC = () => {
  const [page, setPage] = useState(0);
  const [pinnedCard, setPinnedCard] = useState<string | null>(null);
  const [openInfoCard, setOpenInfoCard] = useState<string | null>(null);
  const m = useLiveMetrics();

  const kpis = buildKPIs(m);
  const PAGE_COUNT = Math.ceil(kpis.length / PAGE_SIZE);
  const visible = kpis.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  // Freeze auto-rotation when a card is pinned or an info popover is open
  const frozen = pinnedCard !== null || openInfoCard !== null;

  useEffect(() => {
    if (frozen) return;
    const timer = setInterval(() => {
      setPage(p => (p + 1) % PAGE_COUNT);
    }, 8000);
    return () => clearInterval(timer);
  }, [PAGE_COUNT, frozen]);

  // Unpin if the carousel advances to a new page (pinned card no longer visible)
  useEffect(() => {
    if (pinnedCard && !visible.find(k => k.label === pinnedCard)) {
      setPinnedCard(null);
    }
  }, [page, pinnedCard, visible]);

  return (
    <section style={{ background: '#0D0D0D' }} className="border-t border-b border-white/5 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <Activity size={15} className="text-[#F7931A]" />
          <h2 className="text-base font-bold text-white">On-Chain Pulse</h2>
          <div className="flex items-center gap-1.5 ml-1">
            <div
              className="w-1.5 h-1.5 rounded-full"
              style={{
                backgroundColor: m.loading ? '#888' : '#00C896',
                animation: m.loading ? 'none' : 'pulse 2s infinite',
              }}
            />
            <span
              className="text-xs font-medium"
              style={{ color: m.loading ? '#888' : '#00C896' }}
            >
              {m.loading ? 'Loading…' : 'Live'}
            </span>
          </div>
          {m.lastUpdated && !m.loading && (
            <span className="text-gray-700 text-xs">
              Updated {m.lastUpdated.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          {/* Page label */}
          <span className="text-gray-700 text-xs ml-auto">
            {page + 1} / {PAGE_COUNT}
          </span>
        </div>

        {/* Error banner */}
        {m.error && (
          <div
            className="mb-4 px-3 py-2 rounded-lg text-xs"
            style={{ background: '#FF444415', color: '#FF8888', border: '1px solid #FF444430' }}
          >
            ⚠️ Some data could not be loaded. Showing cached or placeholder values.
          </div>
        )}

        {/* Cards */}
        <AnimatePresence mode="wait">
          <motion.div
            key={page}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-3"
          >
            {m.loading
              ? Array.from({ length: PAGE_SIZE }).map((_, i) => <SkeletonCard key={i} />)
              : visible.map(kpi => (
                <KPICard
                  key={kpi.label}
                  kpi={kpi}
                  isPinned={pinnedCard === kpi.label}
                  onPin={() => setPinnedCard(prev => prev === kpi.label ? null : kpi.label)}
                  onInfoToggle={(open) => setOpenInfoCard(open ? kpi.label : null)}
                />
              ))
            }
          </motion.div>
        </AnimatePresence>

        {/* Dot indicators */}
        <div className="flex items-center justify-center gap-1.5 mt-4">
          {Array.from({ length: PAGE_COUNT }).map((_, i) => (
            <button
              key={i}
              onClick={() => setPage(i)}
              className="rounded-full transition-all"
              style={{
                width: i === page ? '20px' : '6px',
                height: '6px',
                backgroundColor: i === page ? '#F7931A' : 'rgba(255,255,255,0.15)',
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default OnChainSnapshot;
